import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const POLL_MS = 30 * 1000;

/**
 * Keeps long-lived caches honest: polls a single timestamp (max updated_at
 * across videos/channels/creators) and, when it changes, invalidates every
 * cached query so the UI picks up backoffice edits within ~30 seconds.
 * Also re-checks when the tab regains focus.
 */
export function useContentFreshness() {
  const queryClient = useQueryClient();
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled || typeof document === "undefined" || document.hidden) return;
      const { data, error } = await supabase.rpc("content_revision");
      if (cancelled || error) return;
      const rev = (data as unknown as string) ?? "";
      if (lastRef.current === null) {
        lastRef.current = rev;
        return;
      }
      if (rev !== lastRef.current) {
        lastRef.current = rev;
        queryClient.invalidateQueries();
      }
    };

    void check();
    const interval = window.setInterval(check, POLL_MS);
    const onVisible = () => void check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [queryClient]);
}
